/**
 * workflow-core
 *
 * Copyright (c) Ballerine
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * @license MIT
 */
import { inheritsLoose as _inheritsLoose, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../_virtual/_rollupPluginBabelHelpers.js';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { z } from 'zod';
import { PrimitiveSchema, PrimitiveArraySchema, BetweenSchema, LastYearsSchema } from './schemas.js';
import { ValidationFailedError, DataValueNotFoundError } from '../errors.js';
import { OPERATORS_WITHOUT_PATH_COMPARISON, OperationHelpers } from './constants.js';

var BaseOperator = /*#__PURE__*/function () {
  function BaseOperator(options) {
    var operator = options.operator,
      conditionValueSchema = options.conditionValueSchema,
      dataValueSchema = options.dataValueSchema;
    this.operator = operator;
    this.conditionValueSchema = conditionValueSchema;
    this.dataValueSchema = dataValueSchema;
  }
  var _proto = BaseOperator.prototype;
  _proto.extractValue = function extractValue(data, rule, options) {
    var value = get(data, rule.key || '');
    var isPathComparison = !OPERATORS_WITHOUT_PATH_COMPARISON.includes(rule.operator) && 'isPathComparison' in rule && rule.isPathComparison;
    if (!isPathComparison) {
      if (value === undefined || value === null) {
        throw new DataValueNotFoundError(rule.key || '');
      }
      return value;
    }
    var comparisonValueAsPath = rule.value;
    var evaluatedComparisonValue = get(data, comparisonValueAsPath);
    if (evaluatedComparisonValue === undefined || evaluatedComparisonValue === null) {
      throw new DataValueNotFoundError(comparisonValueAsPath);
    }
    return {
      value: value,
      comparisonValue: evaluatedComparisonValue
    };
  };
  _proto.execute = /*#__PURE__*/function () {
    var _execute = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(dataValue, conditionValue, options) {
      var result;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return this.validate({
              dataValue: dataValue,
              conditionValue: conditionValue
            });
          case 2:
            _context.next = 4;
            return this.evaluate(dataValue, conditionValue, options);
          case 4:
            result = _context.sent;
            if (!(result instanceof Promise)) {
              _context.next = 11;
              break;
            }
            _context.next = 8;
            return result;
          case 8:
            _context.t0 = _context.sent;
            _context.next = 12;
            break;
          case 11:
            _context.t0 = result;
          case 12:
            return _context.abrupt("return", _context.t0);
          case 13:
          case "end":
            return _context.stop();
        }
      }, _callee, this);
    }));
    function execute(_x, _x2, _x3) {
      return _execute.apply(this, arguments);
    }
    return execute;
  }();
  _proto.validate = /*#__PURE__*/function () {
    var _validate = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(args) {
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            if (!this.conditionValueSchema) {
              _context2.next = 3;
              break;
            }
            _context2.next = 3;
            return this.validateSchema(this.conditionValueSchema, args.conditionValue, "Invalid condition value");
          case 3:
            if (!this.dataValueSchema) {
              _context2.next = 6;
              break;
            }
            _context2.next = 6;
            return this.validateSchema(this.dataValueSchema, args.dataValue, "Invalid data value");
          case 6:
          case "end":
            return _context2.stop();
        }
      }, _callee2, this);
    }));
    function validate(_x4) {
      return _validate.apply(this, arguments);
    }
    return validate;
  }();
  _proto.validateSchema = /*#__PURE__*/function () {
    var _validateSchema = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee3(schema, value, message) {
      var result;
      return _regeneratorRuntime().wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            result = schema.safeParse(value);
            if (result.success) {
              _context3.next = 3;
              break;
            }
            throw new ValidationFailedError(this.operator, message, result.error);
          case 3:
          case "end":
            return _context3.stop();
        }
      }, _callee3, this);
    }));
    function validateSchema(_x5, _x6, _x7) {
      return _validateSchema.apply(this, arguments);
    }
    return validateSchema;
  }();
  return BaseOperator;
}();
var Equals = /*#__PURE__*/function (_BaseOperator) {
  function Equals() {
    var _this;
    _this = _BaseOperator.call(this, {
      operator: 'EQUALS',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this.evaluate = function (dataValue, conditionValue) {
      return dataValue === conditionValue;
    };
    return _this;
  }
  _inheritsLoose(Equals, _BaseOperator);
  return Equals;
}(BaseOperator);
var NotEquals = /*#__PURE__*/function (_BaseOperator2) {
  function NotEquals() {
    var _this2;
    _this2 = _BaseOperator2.call(this, {
      operator: 'NOT_EQUALS',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this2.evaluate = function (dataValue, conditionValue) {
      return dataValue !== conditionValue;
    };
    return _this2;
  }
  _inheritsLoose(NotEquals, _BaseOperator2);
  return NotEquals;
}(BaseOperator);
var In = /*#__PURE__*/function (_BaseOperator3) {
  function In() {
    var _this3;
    _this3 = _BaseOperator3.call(this, {
      operator: 'IN',
      conditionValueSchema: PrimitiveArraySchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this3.evaluate = function (dataValue, conditionValue) {
      return conditionValue.includes(dataValue);
    };
    return _this3;
  }
  _inheritsLoose(In, _BaseOperator3);
  return In;
}(BaseOperator);
var InCaseInsensitive = /*#__PURE__*/function (_BaseOperator4) {
  function InCaseInsensitive() {
    var _this4;
    _this4 = _BaseOperator4.call(this, {
      operator: 'IN_CASE_INSENSITIVE',
      dataValueSchema: z.union([PrimitiveSchema, PrimitiveArraySchema]),
      conditionValueSchema: PrimitiveArraySchema
    }) || this;
    _this4.evaluate = function (dataValue, conditionValue) {
      var lowercaseDataValue = Array.isArray(dataValue) ? dataValue.map(function (item) {
        return typeof item === 'string' ? item.toLowerCase() : item;
      }) : dataValue;
      if (typeof lowercaseDataValue === 'string') {
        lowercaseDataValue = lowercaseDataValue.toLowerCase();
      }
      return conditionValue.some(function (item) {
        var lowercasedItem = typeof item === 'string' ? item.toLowerCase() : item;
        var checkValue = function checkValue(value) {
          if (typeof value === 'string') {
            return value.includes(lowercasedItem.toString());
          }
          return value === lowercasedItem;
        };
        if (Array.isArray(lowercaseDataValue)) {
          return lowercaseDataValue.some(checkValue);
        }
        return checkValue(lowercaseDataValue);
      });
    };
    return _this4;
  }
  _inheritsLoose(InCaseInsensitive, _BaseOperator4);
  return InCaseInsensitive;
}(BaseOperator);
var NotIn = /*#__PURE__*/function (_BaseOperator5) {
  function NotIn() {
    var _this5;
    _this5 = _BaseOperator5.call(this, {
      operator: 'NOT_IN',
      conditionValueSchema: PrimitiveArraySchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this5.evaluate = function (dataValue, conditionValue) {
      return !conditionValue.includes(dataValue);
    };
    return _this5;
  }
  _inheritsLoose(NotIn, _BaseOperator5);
  return NotIn;
}(BaseOperator);
var GreaterThan = /*#__PURE__*/function (_BaseOperator6) {
  function GreaterThan() {
    var _this6;
    _this6 = _BaseOperator6.call(this, {
      operator: 'GT',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this6.evaluate = function (dataValue, conditionValue) {
      return dataValue > conditionValue;
    };
    return _this6;
  }
  _inheritsLoose(GreaterThan, _BaseOperator6);
  return GreaterThan;
}(BaseOperator);
var LessThan = /*#__PURE__*/function (_BaseOperator7) {
  function LessThan() {
    var _this7;
    _this7 = _BaseOperator7.call(this, {
      operator: 'LT',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this7.evaluate = function (dataValue, conditionValue) {
      return dataValue < conditionValue;
    };
    return _this7;
  }
  _inheritsLoose(LessThan, _BaseOperator7);
  return LessThan;
}(BaseOperator);
var GreaterThanOrEqual = /*#__PURE__*/function (_BaseOperator8) {
  function GreaterThanOrEqual() {
    var _this8;
    _this8 = _BaseOperator8.call(this, {
      operator: 'GTE',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this8.evaluate = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee4(dataValue, conditionValue) {
        return _regeneratorRuntime().wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              _context4.next = 2;
              return _this8.equals.execute(dataValue, conditionValue);
            case 2:
              _context4.t0 = _context4.sent;
              if (_context4.t0) {
                _context4.next = 7;
                break;
              }
              _context4.next = 6;
              return _this8.greaterThan.execute(dataValue, conditionValue);
            case 6:
              _context4.t0 = _context4.sent;
            case 7:
              return _context4.abrupt("return", _context4.t0);
            case 8:
            case "end":
              return _context4.stop();
          }
        }, _callee4);
      }));
      return function (_x8, _x9) {
        return _ref.apply(this, arguments);
      };
    }();
    _this8.equals = new Equals();
    _this8.greaterThan = new GreaterThan();
    return _this8;
  }
  _inheritsLoose(GreaterThanOrEqual, _BaseOperator8);
  return GreaterThanOrEqual;
}(BaseOperator);
var LessThanOrEqual = /*#__PURE__*/function (_BaseOperator9) {
  function LessThanOrEqual() {
    var _this9;
    _this9 = _BaseOperator9.call(this, {
      operator: 'LTE',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this9.evaluate = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee5(dataValue, conditionValue) {
        return _regeneratorRuntime().wrap(function _callee5$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              _context5.next = 2;
              return _this9.equals.execute(dataValue, conditionValue);
            case 2:
              _context5.t0 = _context5.sent;
              if (_context5.t0) {
                _context5.next = 7;
                break;
              }
              _context5.next = 6;
              return _this9.lessThan.execute(dataValue, conditionValue);
            case 6:
              _context5.t0 = _context5.sent;
            case 7:
              return _context5.abrupt("return", _context5.t0);
            case 8:
            case "end":
              return _context5.stop();
          }
        }, _callee5);
      }));
      return function (_x10, _x11) {
        return _ref2.apply(this, arguments);
      };
    }();
    _this9.equals = new Equals();
    _this9.lessThan = new LessThan();
    return _this9;
  }
  _inheritsLoose(LessThanOrEqual, _BaseOperator9);
  return LessThanOrEqual;
}(BaseOperator);
var Between = /*#__PURE__*/function (_BaseOperator10) {
  function Between() {
    var _this10;
    _this10 = _BaseOperator10.call(this, {
      operator: 'BETWEEN',
      conditionValueSchema: BetweenSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this10.evaluate = /*#__PURE__*/function () {
      var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee6(dataValue, conditionValue) {
        return _regeneratorRuntime().wrap(function _callee6$(_context6) {
          while (1) switch (_context6.prev = _context6.next) {
            case 0:
              _context6.next = 2;
              return _this10.gte.execute(dataValue, conditionValue.min);
            case 2:
              _context6.t0 = _context6.sent;
              if (!_context6.t0) {
                _context6.next = 7;
                break;
              }
              _context6.next = 6;
              return _this10.lte.execute(dataValue, conditionValue.max);
            case 6:
              _context6.t0 = _context6.sent;
            case 7:
              return _context6.abrupt("return", _context6.t0);
            case 8:
            case "end":
              return _context6.stop();
          }
        }, _callee6);
      }));
      return function (_x12, _x13) {
        return _ref3.apply(this, arguments);
      };
    }();
    _this10.gte = new GreaterThanOrEqual();
    _this10.lte = new LessThanOrEqual();
    return _this10;
  }
  _inheritsLoose(Between, _BaseOperator10);
  return Between;
}(BaseOperator);
var LastYear = /*#__PURE__*/function (_BaseOperator11) {
  function LastYear() {
    var _this11;
    _this11 = _BaseOperator11.call(this, {
      operator: 'LAST_YEAR',
      conditionValueSchema: LastYearsSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this11.evaluate = function (dataValue, conditionValue) {
      if (typeof dataValue === 'string' || dataValue instanceof Date) {
        var date = new Date(dataValue);
        var yearsAgo = new Date();
        yearsAgo.setFullYear(yearsAgo.getFullYear() - conditionValue.years);
        yearsAgo.setHours(0, 0, 0, 0);
        return date >= yearsAgo;
      }
      throw new ValidationFailedError(_this11.operator, "Unsupported data type " + typeof dataValue);
    };
    return _this11;
  }
  _inheritsLoose(LastYear, _BaseOperator11);
  return LastYear;
}(BaseOperator);
/*
  @deprecated - not in use
*/
var Exists = /*#__PURE__*/function (_BaseOperator12) {
  function Exists() {
    var _this12;
    _this12 = _BaseOperator12.call(this, {
      operator: 'EXISTS'
    }) || this;
    _this12.evaluate = function (dataValue, conditionValue) {
      if (conditionValue.schema) {
        var result = conditionValue.schema.safeParse(dataValue);
        if (!result.success) {
          return false;
        }
      }
      return !isEmpty(dataValue);
    };
    return _this12;
  }
  _inheritsLoose(Exists, _BaseOperator12);
  return Exists;
}(BaseOperator);
var AmlCheck = /*#__PURE__*/function (_BaseOperator13) {
  function AmlCheck() {
    var _this13;
    _this13 = _BaseOperator13.call(this, {
      operator: 'AML_CHECK'
    }) || this;
    _this13.evaluate = /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee7(dataValue, conditionValue) {
        var amlOperator, evaluateOperatorCheck;
        return _regeneratorRuntime().wrap(function _callee7$(_context7) {
          while (1) switch (_context7.prev = _context7.next) {
            case 0:
              amlOperator = OperationHelpers[conditionValue.operator];
              evaluateOperatorCheck = function evaluateOperatorCheck(data) {
                var _amlOperator$dataValu, _amlOperator$conditio;
                var result = (_amlOperator$dataValu = amlOperator.dataValueSchema) == null ? void 0 : _amlOperator$dataValu.safeParse(data);
                if (result && !result.success) {
                  return false;
                }
                var conditionResult = (_amlOperator$conditio = amlOperator.conditionValueSchema) == null ? void 0 : _amlOperator$conditio.safeParse(conditionValue.value);
                if (conditionResult && !conditionResult.success || !(conditionResult != null && conditionResult.data)) {
                  return false; // TODO: throw explicit error
                }
                return amlOperator.execute(data, conditionResult.data);
              };
              if (!(dataValue && Array.isArray(dataValue))) {
                _context7.next = 6;
                break;
              }
              return _context7.abrupt("return", dataValue.some(evaluateOperatorCheck));
            case 6:
              return _context7.abrupt("return", evaluateOperatorCheck(dataValue));
            case 7:
            case "end":
              return _context7.stop();
          }
        }, _callee7);
      }));
      return function (_x14, _x15) {
        return _ref4.apply(this, arguments);
      };
    }();
    return _this13;
  }
  _inheritsLoose(AmlCheck, _BaseOperator13);
  var _proto2 = AmlCheck.prototype;
  _proto2.extractValue = function extractValue(data, rule) {
    var amlRule = rule;
    var result = z.record(z.string(), z.any()).safeParse(data);
    if (!result.success) {
      throw new ValidationFailedError('extract', 'parsing failed', result.error);
    }
    var objData = result.data;
    var childWorkflows = objData.childWorkflows[amlRule.value.childWorkflowName];
    var childWorkflowKeys = childWorkflows ? Object.keys(childWorkflows || {}) : [];
    var hits = childWorkflowKeys.map(function (workflowId) {
      return get(childWorkflows, workflowId + ".result.vendorResult.aml.hits");
    }).flat(1).filter(Boolean);
    if (isEmpty(hits)) {
      throw new DataValueNotFoundError(rule.key);
    }
    return hits.map(function (hit) {
      return get(hit, rule.key);
    }).filter(Boolean);
  };
  return AmlCheck;
}(BaseOperator);
var FuzzyMatchScoreLt = /*#__PURE__*/function (_BaseOperator14) {
  function FuzzyMatchScoreLt() {
    var _this14;
    _this14 = _BaseOperator14.call(this, {
      operator: 'FUZZY_MATCH_SCORE_LT',
      conditionValueSchema: PrimitiveSchema,
      dataValueSchema: PrimitiveSchema
    }) || this;
    _this14.evaluate = /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee8(dataValue, conditionValue, options) {
        var _options$threshold, _response$data, _response$data2;
        var threshold, response;
        return _regeneratorRuntime().wrap(function _callee8$(_context8) {
          while (1) switch (_context8.prev = _context8.next) {
            case 0:
              threshold = (_options$threshold = options.threshold) != null ? _options$threshold : 0;
              if (!(typeof threshold !== 'number' || threshold < 0 || threshold > 100)) {
                _context8.next = 3;
                break;
              }
              throw new Error(_this14.operator + ": Threshold must be a number between 0 and 100");
            case 3:
              _context8.next = 5;
              return options.unifiedApiClient.runEntityMatchingV2({
                entity1: dataValue.toString(),
                entity2: conditionValue.toString(),
                includeAnalysis: false
              });
            case 5:
              response = _context8.sent;
              if (!(!(response != null && (_response$data = response.data) != null && _response$data.similarityScore) && (response == null ? void 0 : (_response$data2 = response.data) == null ? void 0 : _response$data2.similarityScore) !== 0)) {
                _context8.next = 8;
                break;
              }
              throw new Error(_this14.operator + ": Missing similarity score in response");
            case 8:
              return _context8.abrupt("return", response.data.similarityScore < threshold);
            case 9:
            case "end":
              return _context8.stop();
          }
        }, _callee8);
      }));
      return function (_x16, _x17, _x18) {
        return _ref5.apply(this, arguments);
      };
    }();
    return _this14;
  }
  _inheritsLoose(FuzzyMatchScoreLt, _BaseOperator14);
  return FuzzyMatchScoreLt;
}(BaseOperator);
var UboMismatch = /*#__PURE__*/function (_BaseOperator15) {
  function UboMismatch() {
    var _this15;
    _this15 = _BaseOperator15.call(this, {
      operator: 'UBO_MISMATCH'
    }) || this;
    _this15.evaluate = function (data) {
      var collectionUbos = data.collectionUbos,
        registryUbos = data.registryUbos;
      var exactMatch = collectionUbos.length === registryUbos.length && collectionUbos.every(function (name, index) {
        return name === registryUbos[index];
      });
      return !exactMatch;
    };
    return _this15;
  }
  _inheritsLoose(UboMismatch, _BaseOperator15);
  var _proto3 = UboMismatch.prototype;
  _proto3.extractValue = function extractValue(data) {
    try {
      var normalizedString = z.string().transform(function (name) {
        return name.toUpperCase().trim();
      });
      var result = z.object({
        entity: z.object({
          data: z.object({
            additionalInfo: z.object({
              ubos: z.array(z.object({
                firstName: normalizedString,
                lastName: normalizedString
              }))
            })
          })
        }),
        pluginsOutput: z.object({
          ubo: z.object({
            data: z.object({
              nodes: z.array(z.object({
                data: z.object({
                  name: normalizedString,
                  type: z.string()
                })
              })).transform(function (nodes) {
                return nodes.filter(function (node) {
                  return node.data.type === 'PERSON';
                });
              })
            })
          })
        })
      }).parse(data);
      return {
        collectionUbos: result.entity.data.additionalInfo.ubos.map(function (ubo) {
          return ubo.firstName + " " + ubo.lastName;
        }).sort(),
        registryUbos: result.pluginsOutput.ubo.data.nodes.map(function (node) {
          return node.data.name;
        }).sort()
      };
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new ValidationFailedError('extract', 'parsing failed', error);
      }
      throw error;
    }
  };
  return UboMismatch;
}(BaseOperator);
var EQUALS = new Equals();
var NOT_EQUALS = new NotEquals();
var EXISTS = new Exists();
var GT = new GreaterThan();
var LT = new LessThan();
var GTE = new GreaterThanOrEqual();
var LTE = new LessThanOrEqual();
var BETWEEN = new Between();
var LAST_YEAR = new LastYear();
var IN = new In();
var IN_CASE_INSENSITIVE = new InCaseInsensitive();
var NOT_IN = new NotIn();
var AML_CHECK = new AmlCheck();
var FUZZY_MATCH_SCORE_LT = new FuzzyMatchScoreLt();
var UBO_MISMATCH = new UboMismatch();

export { AML_CHECK, AmlCheck, BETWEEN, BaseOperator, Between, EQUALS, EXISTS, Equals, Exists, FUZZY_MATCH_SCORE_LT, FuzzyMatchScoreLt, GT, GTE, GreaterThan, GreaterThanOrEqual, IN, IN_CASE_INSENSITIVE, In, InCaseInsensitive, LAST_YEAR, LT, LTE, LastYear, LessThan, LessThanOrEqual, NOT_EQUALS, NOT_IN, NotEquals, NotIn, UBO_MISMATCH };
//# sourceMappingURL=helpers.js.map
